import {IconButton} from '@wandb/components';
// @todo: Migrate to 'Select' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {WBSelect} from '@wandb/weave/common/components/WBSelect';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import {clsx} from 'clsx';
import React, {type ReactNode} from 'react';

import {EMPTY_CONFIG} from '../../../util/runfeed';

interface WBTablePaginationProps {
  loading: boolean;
  currentPage: number;
  maxPage: number;
  currentPageSize?: number;
  totalRowCount: number;
  onClickPrevPage(): void;
  onClickNextPage(): void;
  setPageSize(newPageSize: number): void;
}

const options = [10, 20, 50, 100].map(size => ({
  name: `${size}`,
  value: size,
  icon: null,
  'data-test': 'pageSize-set',
}));

export function WBTablePagination({
  loading,
  currentPage,
  maxPage,
  onClickPrevPage,
  onClickNextPage,
  setPageSize,
  totalRowCount,
  currentPageSize = EMPTY_CONFIG.pageSize,
}: WBTablePaginationProps) {
  function getDisplayedValue(): string {
    if (totalRowCount <= 0) {
      return '0';
    }
    const start = 1 + (currentPage - 1) * currentPageSize;
    const end = Math.min(totalRowCount, currentPage * currentPageSize);
    return `${start.toLocaleString()}-${end.toLocaleString()}`;
  }

  const isPrevDisabled = loading || currentPage === 1;
  const isNextDisabled = loading || currentPage === maxPage;

  return (
    <div className="flex justify-end">
      <div className="flex gap-6 items-center py-6 px-8">
        <WBSelect
          className={clsx('flex-grow-0', {'opacity-60': loading})}
          data-test="pageSize-popup"
          aria-label="Select page size"
          disabled={loading}
          menuTheme="light"
          menuFontSize={16}
          menuLineHeight={24}
          options={options}
          value={currentPageSize}
          displayedValue={getDisplayedValue()}
          autoMenuWidth
          onSelect={value => {
            if (typeof value === 'number') {
              setPageSize(value);
            } else {
              setPageSize(parseInt(value, 10));
            }
          }}
        />
        <span className="whitespace-nowrap">
          of {totalRowCount.toLocaleString()}
        </span>
        <div className="shrink-0">
          <IconButton
            icon="chevron-back"
            aria-label="Go to previous page"
            isDisabled={isPrevDisabled}
            onPress={onClickPrevPage}
            variant="ghost"
          />
          <IconButton
            icon="chevron-next"
            aria-label="Go to next page"
            isDisabled={isNextDisabled}
            onPress={onClickNextPage}
            variant="ghost"
          />
        </div>
      </div>
    </div>
  );
}

type WBTablePaginationContainerProps = {
  children: ReactNode;
  floating: boolean;
};

export function WBTablePaginationContainer({
  children,
  floating,
}: WBTablePaginationContainerProps) {
  if (!floating) {
    return <TailwindContents>{children}</TailwindContents>;
  }

  return (
    <TailwindContents>
      <div className="sticky bottom-0 height-0 z-[105]">
        <div className="bg-white shadow-[0_2px_6px_rgba(0,0,0,0.1)] right-16 bottom-8 absolute rounded-lg">
          {children}
        </div>
      </div>
    </TailwindContents>
  );
}
